import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import MediaCardNodeView from './MediaCardNodeView';

/**
 * Media Card Extension for TipTap
 * 
 * Supports embedding various media types as interactive cards:
 * - Images with zoom and preview
 * - Videos with playback controls
 * - Audio with waveform visualization
 * - PDFs with preview
 */
const MediaCardExtension = Node.create({
    name: 'mediaCard',

    group: 'block',

    atom: true, // This node doesn't have editable content

    draggable: true,

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    addAttributes() {
        return {
            src: {
                default: null,
                parseHTML: element => element.getAttribute('data-src'),
                renderHTML: attributes => {
                    if (!attributes.src) return {};
                    return { 'data-src': attributes.src };
                },
            },
            title: {
                default: null,
                parseHTML: element => element.getAttribute('data-title'),
                renderHTML: attributes => {
                    if (!attributes.title) return {};
                    return { 'data-title': attributes.title };
                },
            },
            assetType: {
                default: 'document',
                parseHTML: element => element.getAttribute('data-asset-type'),
                renderHTML: attributes => {
                    return { 'data-asset-type': attributes.assetType };
                },
            },
            fileName: {
                default: null,
                parseHTML: element => element.getAttribute('data-file-name'),
                renderHTML: attributes => {
                    if (!attributes.fileName) return {};
                    return { 'data-file-name': attributes.fileName };
                },
            },
            assetId: {
                default: null,
                parseHTML: element => element.getAttribute('data-asset-id'),
                renderHTML: attributes => {
                    if (!attributes.assetId) return {};
                    return { 'data-asset-id': attributes.assetId };
                },
            },
            width: {
                default: 80, // Default to 80% width
                parseHTML: element => {
                    const width = element.getAttribute('data-width');
                    return width ? parseInt(width, 10) : 80;
                },
                renderHTML: attributes => {
                    return { 'data-width': attributes.width };
                },
            },
            altText: {
                default: '',
                parseHTML: element => element.getAttribute('data-alt-text'),
                renderHTML: attributes => {
                    if (!attributes.altText) return {};
                    return { 'data-alt-text': attributes.altText };
                },
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption'),
                renderHTML: attributes => {
                    if (!attributes.caption) return {};
                    return { 'data-caption': attributes.caption };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="media-card"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
                'data-type': 'media-card',
                'class': 'media-card-wrapper',
            }),
            // Return empty content - the React NodeView will handle rendering
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(MediaCardNodeView);
    },

    /**
     * Add commands for inserting media cards
     */
    addCommands() {
        return {
            insertMediaCard: (options) => ({ commands }) => {
                return commands.insertContent({
                    type: this.name,
                    attrs: {
                        ...options,
                        width: options.width || 80, // Default to 80% width
                    },
                });
            },
        };
    },
});

export default MediaCardExtension;
